import { HamburgerMenu } from "@/components/HamburgerMenu";
import { DashboardTitle } from "@/components/Header/DashboardTitle";
import { useAuth } from "@/config/auth";
import { useDashboardGlobalContext } from "@/dashboards/providers/dashboardGlobalContext";
import { useDashboardLinks } from "@/hooks/useDashboardLinks";
import { cn } from "@/utils";
import { AnalyticalAlleyLogo, useMatchMedia } from "@analytical-alley/ui";
import { Link } from "@tanstack/react-router";
import React, { ReactNode, useEffect, useState } from "react";
import { AiOutlineFolderOpen, AiOutlineLogout } from "react-icons/ai";

interface Props {
    title: string;
    subtitle?: string | ReactNode;
    children?: ReactNode;
    className?: string;
    sticky?: boolean;
    tooltip?: ReactNode | string;
}

export const Header = ({
    title,
    subtitle,
    children,
    className,
    sticky,
    tooltip,
}: Props) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const { isMd } = useMatchMedia(['lg', 'md']);
    const { allAccessibleLinks } = useDashboardLinks();
    const { isAuthenticated } = useAuth();
    const {
        project: { name },
    } = useDashboardGlobalContext();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };
        if (sticky) window.addEventListener('scroll', handleScroll);

        return () => {
            if (sticky) window.removeEventListener('scroll', handleScroll);
        };
    }, [sticky]);

    return (
        <div
            className={cn('h-28 flex flex-row align-middle', className, {
                glass: isScrolled,
                'sticky top-0': sticky,
                'z-50': isMenuOpen || sticky,
            })}
        >
            <div
                className="lg:w-48 md:min-w-50 md:w-24 w-12 min-w-12.5 relative h-full"
                title={name}
            >
                <AnalyticalAlleyLogo
                    size={isMd ? 'normal' : 'small'}
                    min={!isMd}
                    className={cn('absolute opacity-100', {
                        'opacity-30': isMenuOpen,
                    })}
                />
            </div>
            <DashboardTitle title={title} subtitle={subtitle} isLg={isMd} tooltip={tooltip} />
            <div className="grow" />
            <div className="flex gap-4">{children}</div>
            <HamburgerMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <div className="grid gap-2.5 glass-tile rounded-xl p-3 max-w-full min-w-48">
                    <ul className="space-y-1">
                        {allAccessibleLinks.map(
                            ({ Link, title, icon, category, badge }, index) => {
                                const previousCategory =
                                    allAccessibleLinks[index - 1]?.category;
                                const showCategoryHeading =
                                    category && category !== previousCategory;

                                return (
                                    <React.Fragment key={title}>
                                        {showCategoryHeading && (
                                            <li
                                                className={cn(
                                                    'px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/50',
                                                    { 'pt-2': index !== 0 },
                                                )}
                                            >
                                                {category}
                                            </li>
                                        )}
                                        <li>
                                            <Link
                                                activeProps={{
                                                    className:
                                                        'bg-white/30 cursor-default flex items-center gap-2 p-2 rounded-md text-nowrap',
                                                    href: '',
                                                }}
                                                inactiveProps={{
                                                    className:
                                                        'hover:bg-white/20 cursor-pointer flex items-center gap-2 p-2 rounded-md text-nowrap',
                                                }}
                                                activeOptions={{
                                                    exact: true,
                                                    includeSearch: false,
                                                }}
                                            >
                                                {icon}
                                                {title}
                                                {badge}
                                            </Link>
                                        </li>
                                    </React.Fragment>
                                );
                            },
                        )}
                    </ul>
                    <div className="grid gap-1 rounded-md bg-black/20 p-1.5 mt-1">
                        <Link
                            className="hover:bg-white/20 cursor-pointer flex items-center gap-2 p-2 rounded-md text-nowrap text-xs font-semibold uppercase tracking-wider text-white/70 hover:text-white"
                            to={'/'}
                        >
                            <AiOutlineFolderOpen className="text-base" />
                            Projects
                        </Link>
                        {isAuthenticated && (
                            <Link
                                className="hover:bg-white/20 cursor-pointer flex items-center gap-2 p-2 rounded-md text-nowrap text-xs font-semibold uppercase tracking-wider text-white/70 hover:text-white"
                                to={'/auth/logout'}
                            >
                                <AiOutlineLogout className="text-base" />
                                Logout
                            </Link>
                        )}
                    </div>
                </div>
            </HamburgerMenu>
        </div>
    );
};
